import Scene = Phaser.Scene;
import Text = Phaser.GameObjects.Text;
import Container = Phaser.GameObjects.Container;
import Image = Phaser.GameObjects.Image;
import Sprite = Phaser.GameObjects.Sprite;
import BaseSound = Phaser.Sound.BaseSound;
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {SpaceTr, Tr, TrSpace} from "@/i18n/i18n";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Link} from "@/scenes.ui/link";
import {NewBoosterPanel} from "@/scenes.ui/new-booster-panel";
import {SquareButton} from "@/scenes.ui/square-button";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {Creature} from "@/store/level/types";
import {BonusStatus, ItemType, PurchaseInfo, PurchaseItem} from "@/store/shop/types";
import CU from "@/utils/c-u";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import TextUtils from "@/utils/text-utils";
import {StringUtils} from '@/utils/string-utils';
import {CardPanel} from "@/scenes.ui/card-panel";
import {CardType} from "@/store/card/types";

export class PurchasePanel extends VerticalStandardPanel {

    private itemPanel: Container | undefined;
    private buyMoreGemsText: Text | undefined;
    private buyMoreGemsDetails: Link | undefined;

    private lighting!: Sprite;

    private static circleShift: number = 25;
    private circleInPos!: number;

    private completeSound!: BaseSound;
    private clickSound!: BaseSound;

    constructor(scene: Scene, canvas: HTMLCanvasElement, pi: PurchaseInfo, showManual: () => unknown,
                completeSound: BaseSound, clickSound: BaseSound) {
        super(scene, canvas, Tr('get.p'));
        this.completeSound = completeSound;
        this.clickSound = clickSound;
        this.background.setAlpha(0.1);
        this.xButton.setVisible(false);
        this.init(pi, showManual);
        SoundUtils.play(this.completeSound);
    }

    private init(pi: PurchaseInfo, showManual: () => unknown) {
        let cnt = pi.items.length + pi.creatures.length;
        let pos = -(PurchasePanel.circleShift * (cnt - 1)) / 2;
        this.circleInPos = pos;

        for (let i = 0; i < cnt; i++) {
            let circle = SpriteUtils.addImage(this.scene!, pos, 70, Keys.FRM_RADIO_OUT, undefined, 0.35).setOrigin(0.5, 0.5).setAlpha(0.9);
            this.add(circle);
            pos += PurchasePanel.circleShift;
        }

        let cps: Container[] | Image[];

        const creaturesCopy = Object.assign([], pi.creatures).reverse();
        const itemsCopy = Object.assign([], pi.items).reverse();

        const next = () => {
            const c = creaturesCopy.pop();
            if (c) {
                cps = this.initCreature(c);
            } else {
                const it = itemsCopy.pop();
                if (it) {
                    cps = this.initItem(pi, it, showManual);
                } else {
                    this.close();
                }
            }
        };
        next();

        let okEnabled = true;

        let okButton = new SquareButton(this.scene, 0, 160, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('prinjat').toLocaleUpperCase(), false);
        okButton.setCallback(() => {

            if (!okEnabled) {
                return;
            }
            SoundUtils.play(this.clickSound);
            okEnabled = false;

            let i = 0;
            for (let cp of cps) {
                this.scene.tweens.add({
                    targets: cp,
                    duration: 2200,
                    paused: true,
                    props: {
                        x: {value: cp.x + [150, 200, 50, 175, 25][i], ease: 'Quint.easeOut'},
                        y: {value: cp.y + [700, 600, 800, 750, 725][i], ease: 'Linear'},
                        alpha: 0,
                        scale: 0.3
                    }
                }).play();
                ++i;
            }

            this.scene.time.delayedCall(150, () => {
                okEnabled = true;
                if (this.scene) {
                    let circleIn = SpriteUtils.addImage(this.scene!, this.circleInPos, 70, Keys.FRM_RADIO_IN, undefined, 0.35)
                        .setOrigin(0.5, 0.5).setAlpha(0.9);
                    this.circleInPos += PurchasePanel.circleShift;
                    this.add(circleIn);
                    next();
                }
            });
        });
        this.add(okButton);

        this.setOnCLoseCallback(() => {
            if (pi.booster){
                new NewBoosterPanel(this.scene, this.canvas, pi.booster).open();
            }
        });
    }

    protected preInit(): void {
        this.lighting = SpriteUtils.addSprite(this.scene, 0, 0, Keys.OFFER_LIGHT_BACK, undefined, 4.6).setAlpha(0);
        this.add(this.lighting);

        this.scene!.tweens.add({
            targets: this.lighting,
            alpha: 1,
            duration: 500
        });

        this.scene!.tweens.add({
            targets: this.lighting,
            angle: 360,
            duration: 90000,
            loop: -1,
        });
    }

    protected preClose(): void {
        this.lighting.setVisible(false);
    }

    private initCreature(c: Creature): Container[] {
        this.resetItemPanel();

        let blockFirst = TextUtils.addTextAdvanced(this.scene, Tr('h.w.u'), 0, -175,
            '#a2ff82', 16, 'normal', 400, 'center', 2, Fonts.ARIAL_MT);

        let cp = new CreaturePanel(this.scene, 0, -70, c, () => {}, true);
        SpriteUtils.scale(cp, 0.9);

        let shadow = SpriteUtils.addImage(this.scene!, 0, -10, Keys.PLACE_SHADOW, undefined)
            .setOrigin(0.5, 0.5).setAlpha(0.9);

        let comment = TextUtils.addTextByFamily(this.scene,
            c.name, 0, 20, '#ffcc00', 16, 'bold', 450, Fonts.MYRIADPRO_REGULAR);

        this.itemPanel!.add([blockFirst, shadow, comment]);
        this.add(cp);

        this.scene.load.image(Keys.profileCreature(c.imageId), `assets/profile/creatures/${c.imageId}.png`);
        this.scene.load.start();

        return [cp];
    }

    private initItem(pi: PurchaseInfo, it: PurchaseItem, showManual: () => unknown): Image[] {
        this.resetItemPanel();
        this.resetTitle(it);
        this.resetManual();

        switch (it.itemType) {
            case ItemType.HERO:
                break;
            case ItemType.AZURE_CHEST:
                return this.initOnePictureItem(Tr('az.chst') + '!', String(it.count) + SpaceTr('sst') + '.', undefined, Keys.CHEST_AZURE_CLOSED);
            case ItemType.LAVA_CHEST:
                return this.initOnePictureItem(Tr('lv.chst') + '!', String(it.count) + SpaceTr('sst') + '.', undefined, Keys.CHEST_LAVA_CLOSED);
            case ItemType.SILVER_CHEST:
                return this.initOnePictureItem(Tr('slv.chst') + '!', String(it.count) + SpaceTr('sst') + '.', undefined, Keys.CHEST_SILVER_CLOSED);
            case ItemType.GOLD_CHEST:
                return this.initOnePictureItem(Tr('gld.chst') + '!', String(it.count) + SpaceTr('sst') + '', undefined, Keys.CHEST_GOLDEN_CLOSED);
            case ItemType.AMBER_CHEST:
                return this.initOnePictureItem(Tr('amb.chst') + '!', String(it.count) + SpaceTr('sst') + '', undefined, Keys.CHEST_AMBER_CLOSED);
            case ItemType.JADE_CHEST:
                return this.initOnePictureItem(Tr('jd.chst') + '!', String(it.count) + SpaceTr('sst') + '', undefined, Keys.CHEST_JADE_CLOSED);
            case ItemType.ELEPHANT:
                return this.initOnePictureItem(Tr('card.elephant'), String(it.count) + SpaceTr('sst') + '.', undefined, Keys.CARD_ELEPHANT_SMALL);
            case ItemType.TOKEN:
                return this.initOnePictureItem(Tr('token'), String(it.count) + SpaceTr('sst') + '.', undefined, Keys.TOKEN_BIG);
            case ItemType.SNAKE:
            case ItemType.EAGLE:
            case ItemType.MOUSE:
            case ItemType.EVOLUTION_UNIVERSAL:
            case ItemType.EVOLUTION_ACE:
            case ItemType.EVOLUTION_LUCK:
            case ItemType.EVOLUTION_CHAOS:
            case ItemType.EVOLUTION_DEATH:
            case ItemType.EVOLUTION_DRYAD:
            case ItemType.EVOLUTION_FACELESS:
            case ItemType.EVOLUTION_GORGON:
            case ItemType.EVOLUTION_HUMAN:
            case ItemType.EVOLUTION_INKUB:
            case ItemType.EVOLUTION_KOSCHEY:
            case ItemType.EVOLUTION_MANTICORE:
            case ItemType.EVOLUTION_NUN:
            case ItemType.EVOLUTION_PHOENIX:
            case ItemType.EVOLUTION_RIDER:
            case ItemType.EVOLUTION_SHADOW:
            case ItemType.EVOLUTION_SPHINX:
            case ItemType.EVOLUTION_SUKKUB:
            case ItemType.EVOLUTION_WITCH:
            case ItemType.EVOLUTION_DRUID:
                let ct: CardType = CardType[it.itemType.toString() as keyof typeof CardType];
                let shift = it.itemType === ItemType.EAGLE || it.itemType === ItemType.SNAKE ? 0 : 30;
                return this.initOnePictureItem(CardPanel.getCardName(ct).toUpperCase(), String(it.count) + SpaceTr('sst') + '.',
                    undefined, CardPanel.getCardIcon(ct), 1.0, 20, shift);
            case ItemType.GEMS:
                let firstBlockVis = true;
                let shift2 = 0;

                if (pi.currentStatus === BonusStatus.NONE && pi.statusAfterPurchase === BonusStatus.NONE) {
                    this.buyMoreGemsText = TextUtils.addTextByFamily(this.scene,
                        TrSpace('by.mr') + String(pi.remainTillNextStatus) + SpaceTr('gms.silv'),
                        0, 0, '#a5a5a5', 12, 'bold', 350, Fonts.MYRIADPRO_REGULAR);
                    this.buyMoreGemsDetails = new Link(this.scene, 0, this.buyMoreGemsText.y + 35 * CU.S, Tr('det'), showManual,
                        '#A5A5A5FF', 11, 0.5);
                    this.add([this.buyMoreGemsText, this.buyMoreGemsDetails]);
                    firstBlockVis = false;
                    shift2 = 55;
                }

                return this.initThreePictureItem(Tr('gems.arrived'),
                    String(it.count) + TextUtils.chooseWord(it.count, Tr('izumrud').toUpperCase(), Tr('izumruda').toUpperCase(), Tr('izumrudov').toUpperCase()),
                    firstBlockVis, shift2, Keys.IC_110_MITHRIL);
            case ItemType.GEMS_BONUS:
                return this.initThreePictureItem(Tr('also.bon'),
                    String(it.count) + TextUtils.chooseWord(it.count, Tr('izumrud').toUpperCase(), Tr('izumruda').toUpperCase(), Tr('izumrudov').toUpperCase()),
                    true, 0, Keys.IC_110_MITHRIL);
            case ItemType.GEMS_BONUS_PROGRAM:
                return this.initThreePictureItem(Tr('also.bon.2'),
                    String(it.count) + TextUtils.chooseWord(it.count, Tr('izumrud').toUpperCase(), Tr('izumruda').toUpperCase(), Tr('izumrudov').toUpperCase()),
                    true, 0, Keys.IC_110_MITHRIL);
            case ItemType.ATTACK_COMBATIVE:
                return this.initThreePictureItem(Tr('ataka'), '+' + String(it.count) + '% ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_ATTACK);
            case ItemType.DEFENCE_COMBATIVE:
                return this.initThreePictureItem(Tr('zaschita'), '+' + String(it.count) + '% ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_DEFENCE);
            case ItemType.HEALTH_COMBATIVE:
                return this.initThreePictureItem(Tr('s.h'), '+' + String(it.count) + '% ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_HEALTH);
            case ItemType.HEALTH:
                return this.initThreePictureItem(Tr('s.h'), '+' + String(it.count) + Tr('p.all'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_HEALTH);

            case ItemType.ATTACK_COMBATIVE_ABS:
                return this.initThreePictureItem(Tr('ataka'), '+' + String(it.count) + ' ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_ATTACK);
            case ItemType.DEFENCE_COMBATIVE_ABS:
                return this.initThreePictureItem(Tr('zaschita'), '+' + String(it.count) + ' ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_DEFENCE);
            case ItemType.HEALTH_COMBATIVE_ABS:
                return this.initThreePictureItem(Tr('s.h'), '+' + String(it.count) + ' ' + Tr('cmb'),
                    true, 0, Keys.ATLAS_IC_100, Keys.IC_100_HEALTH);

            case ItemType.MYTH_DISCOUNT:
                return this.initThreePictureItem(Tr('disc.myth') + ' ', String(it.count) + '%', true, 0, Keys.ATLAS_BANK_2,  'discount');
            case ItemType.TICKETS:
                return this.initThreePictureItem(Tr('tick'), String(it.count) + SpaceTr('sst') + '.', true, 0, Keys.IMG_TICKET_BIG);
            case ItemType.CRYSTAL:
                return this.initThreePictureItem(Tr('krasnie.kristalli'), String(it.count), true, 0, 'crystal-1');
            case ItemType.CAVIAR:
                return this.initThreePictureItem(Tr('cav'), String(it.count), true, 0, 'caviar-1');
            case ItemType.ELIXIR:
                return this.initThreePictureItem(Tr('sinie.kristalli'), String(it.count), true, 0, 'elixir-1');
            case ItemType.GOLD:
                return this.initThreePictureItem(Tr('zoloto'), String(it.count), true, 0, 'gold-1');
            case ItemType.EDEM_FREE:
                return this.initOnePictureItem(Tr('p2.i') + '!', Tr('p2.d'), Keys.ATLAS_PORTALS, 'P5', 0.65, 15, 20);
            case ItemType.ATLANT_FREE:
                return this.initOnePictureItem(Tr('p3.i') + '!', Tr('p3.d'), Keys.ATLAS_PORTALS, 'P3', 0.65, 15, 20);
            case ItemType.TROPICS_FREE:
                return this.initOnePictureItem(Tr('p5.i') + '!', Tr('p5.d'), Keys.ATLAS_PORTALS, 'P10', 0.65, 15, 20);
            case ItemType.WATER_FREE:
                return this.initOnePictureItem(Tr('p6.i') + '!', Tr('p6.d'), Keys.ATLAS_PORTALS, 'P12', 0.65, 15, 20);
            case ItemType.MORFEY_FREE:
                return this.initOnePictureItem(Tr('p1.i') + '!', Tr('p1.d'), Keys.ATLAS_PORTALS, 'P1', 0.65, 15, 20);
            case ItemType.SECRET_FREE:
                return this.initOnePictureItem(Tr('p7.i') + '!', Tr('p7.d'), Keys.ATLAS_PORTALS, 'P4', 0.65, 15, 20);
            case ItemType.ATTACK_CLAN:
                return this.initThreePictureItem(Tr('ataka'),  Tr('ataka') + ' +' + String(it.count) + '% ' + Tr('clan.all'), true, 0, Keys.ATL_PACK_SKILLS_BONUS_CLAN, 'attack');
            case ItemType.DEFENCE_CLAN:
                return this.initThreePictureItem(Tr('zaschita'), Tr('zaschita') +  ' +' + String(it.count) + '% ' + Tr('clan.all'), true, 0, Keys.ATL_PACK_SKILLS_BONUS_CLAN, 'defence');
            case ItemType.HEALTH_CLAN:
                return this.initThreePictureItem(Tr('zdorove'), Tr('zdorove') + ' +' + String(it.count) + '% ' + Tr('clan.all'), true, 0, Keys.ATL_PACK_SKILLS_BONUS_CLAN, 'health');

        }

        return [];
    }

    private initOnePictureItem(title: string, desc: string, picAtlas: string | undefined, picKey: string, scale: number = 1.0, descFontSize: number = 20, imageShift: number = 0): Image[] {
        let blockFirst = TextUtils.addTextAdvanced(this.scene, title, 0, -175,
            '#a2ff82', 16, 'normal', 400, 'center', 2, Fonts.ARIAL_MT);

        let pict;

        if (picAtlas) {
            pict = SpriteUtils.addImage(this.scene, 0, blockFirst.y / CU.S + imageShift, picAtlas, picKey).setOrigin(0.5, 0);
        } else {
            pict = SpriteUtils.addImage(this.scene, 0, blockFirst.y / CU.S + imageShift, picKey).setOrigin(0.5, 0);
        }
        SpriteUtils.scale(pict, scale);

        let gems = TextUtils.addTextByFamily(this.scene,
            desc, 0, pict.y / CU.S + 195 - imageShift, '#ffcc00', descFontSize, 'bold', 450, Fonts.MYRIADPRO_REGULAR);

        this.add(pict);
        this.itemPanel!.add([blockFirst, gems]);

        return [pict];
    }

    private initThreePictureItem(title: string, desc: string, firstBlockVisible: boolean, shift2: number, texture: string, key: string | undefined = undefined): Image[] {
        let blockFirst = TextUtils.addTextAdvanced(this.scene, title, 0, -175,
            '#a2ff82', 16, 'normal', 400, 'center', 2, Fonts.ARIAL_MT).setVisible(firstBlockVisible);

        const pict = SpriteUtils.addImage(this.scene, 0, blockFirst.y / CU.S + 45 - shift2, texture, key).setOrigin(0.5, 0);
        const pict2 = SpriteUtils.addImage(this.scene, -92, blockFirst.y / CU.S + 45 - shift2, texture, key).setOrigin(0.5, 0);
        const pict3 = SpriteUtils.addImage(this.scene, 92, blockFirst.y / CU.S + 45 - shift2, texture, key).setOrigin(0.5, 0);

        let shadow1 = SpriteUtils.addImage(this.scene!, 0, pict.y / CU.S + 90, Keys.PLACE_SHADOW, undefined)
            .setOrigin(0.5, 0.5).setAlpha(0.9);
        let shadow2 = SpriteUtils.addImage(this.scene!, -92, pict.y / CU.S + 90, Keys.PLACE_SHADOW, undefined)
            .setOrigin(0.5, 0.5).setAlpha(0.9);
        let shadow3 = SpriteUtils.addImage(this.scene!, 92, pict.y / CU.S + 90, Keys.PLACE_SHADOW, undefined)
            .setOrigin(0.5, 0.5).setAlpha(0.9);

        let gems = TextUtils.addTextByFamily(this.scene,
            desc, 0, pict.y / CU.S + 130, '#ffcc00', 20, 'bold', 450, Fonts.MYRIADPRO_REGULAR);

        this.itemPanel!.add([shadow1, shadow2, shadow3]);
        this.add([pict, pict2, pict3]);
        this.itemPanel!.add([blockFirst, gems]);

        return [pict, pict2, pict3];
    }

    private resetItemPanel() {
        if (this.itemPanel) {
            this.itemPanel!.visible = false;
            this.itemPanel!.destroy();
        }
        this.itemPanel = new Container(this.scene);
        this.add(this.itemPanel);
    }

    private resetManual() {
        if (this.buyMoreGemsText) {
            this.buyMoreGemsText.visible = false;
            this.buyMoreGemsText.destroy();
        }

        if (this.buyMoreGemsDetails) {
            this.buyMoreGemsDetails.visible = false;
            this.buyMoreGemsDetails.destroy();
        }
    }

    private resetTitle(it: PurchaseItem) {
        if (it.giftSenderName) {
            this.setTitle(StringUtils.formatString(Tr('gift.from'), it.giftSenderName));
        } else {
            this.setTitle(Tr('get.p'));
        }
    }

    protected onClose(): void {

    }

}
